import { ArrowLeft } from "lucide-react";
import { useState } from "react";

import {
  calculateRates,
  emptyBaseballStats,
  formatRate,
  RATE_TABLE_COLUMNS,
  SEASON_TABLE_COLUMNS,
  type ArchivedGameStats,
} from "../../domain/baseballStats";
import type { BaseballStats } from "../../shared/types/contracts";
import {
  beerDutyLabel,
  formatScheduleDate,
  homeAwayLabel,
  timeSlotLabel,
} from "../../domain/schedule";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/page-header";
import {
  nextSortState,
  SortableTableHead,
  sortRows,
  type SortState,
} from "@/shared/ui/sortable-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";

type ArchivedSortKey =
  | "PA"
  | (typeof SEASON_TABLE_COLUMNS)[number]
  | (typeof RATE_TABLE_COLUMNS)[number];

type ArchivedBattingRow = {
  name: string;
  stats: BaseballStats;
  pa: number;
  avg: number;
  obp: number;
  slg: number;
  ops: number;
  iso: number;
};

const DEFAULT_SORT: SortState<ArchivedSortKey> = {
  key: "OPS",
  direction: "desc",
};

export function ArchivedGameDetailScreen({
  game,
  onBack,
}: {
  game: ArchivedGameStats | null;
  onBack: () => void;
}) {
  const [sortState, setSortState] = useState(DEFAULT_SORT);

  if (!game) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-3">
        <PageHeader
          title="BOX SCORE"
          eyebrow="Game result"
          actions={
            <Button type="button" size="xs" variant="outline" onClick={onBack}>
              <ArrowLeft aria-hidden="true" /> SCHEDULE
            </Button>
          }
        />
        <p className="rounded-[var(--radius-sm)] border border-dashed border-border px-3 py-4 text-sm text-foreground-muted">
          That game result is no longer available.
        </p>
      </div>
    );
  }
  const result =
    game.ourScore > game.oppScore
      ? "W"
      : game.ourScore < game.oppScore
        ? "L"
        : "T";
  const date = game.scheduledDate ?? game.date;
  const lineScore = game.lineScore ?? { us: [], them: [] };
  const lineup = game.lineup ?? Object.keys(game.stats);
  const battingRows: ArchivedBattingRow[] = lineup.map((name) => {
    const stats = game.stats[name] ?? emptyBaseballStats();
    return {
      name,
      stats,
      pa: stats.AB + stats.BB + stats.SF,
      ...calculateRates(stats),
    };
  });
  const sortedBattingRows = sortRows(battingRows, sortState, archivedSortValue);
  const onSort = (key: ArchivedSortKey) =>
    setSortState((current) => nextSortState(current, key));
  return (
    <div className="mx-auto w-full max-w-5xl space-y-3">
      <PageHeader
        title="BOX SCORE"
        eyebrow="Completed game"
        description={`${formatScheduleDate(date)} · ${timeSlotLabel(game.timeSlot)}`}
        actions={
          <Button type="button" size="xs" variant="outline" onClick={onBack}>
            <ArrowLeft aria-hidden="true" /> SCHEDULE
          </Button>
        }
      />
      <section className="grid gap-2 rounded-[var(--radius-sm)] border border-border bg-surface/65 px-3 py-3 text-sm sm:grid-cols-2">
        <div>
          <strong>
            {game.isHome ? "vs" : "at"} {game.opponent}
          </strong>
          <div className="text-xs text-foreground-muted">
            {homeAwayLabel(game.isHome)} · {game.field ?? "Field unknown"}
          </div>
        </div>
        <div className="sm:text-right">
          <Badge
            variant={
              result === "W" ? "success" : result === "L" ? "danger" : "neutral"
            }
          >
            {result} {game.ourScore}–{game.oppScore}
            {game.forfeitWinner ? " · Forfeit" : ""}
          </Badge>
          <div className="mt-1 text-xs text-foreground-muted">
            Beer: {beerDutyLabel(game.beerDuty)}
          </div>
        </div>
      </section>
      <Table aria-label="Completed game line score">
        <TableHeader>
          <TableRow>
            <TableHead>TEAM</TableHead>
            {lineScore.us.map((_, inning) => (
              <TableHead key={inning}>I{inning + 1}</TableHead>
            ))}
            <TableHead>R</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="font-bold">US</TableCell>
            {lineScore.us.map((runs, inning) => (
              <TableCell key={inning}>{runs}</TableCell>
            ))}
            <TableCell className="font-bold">{game.ourScore}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="font-bold">{game.opponent}</TableCell>
            {lineScore.them.map((runs, inning) => (
              <TableCell key={inning}>{runs}</TableCell>
            ))}
            <TableCell className="font-bold">{game.oppScore}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Table aria-label="Completed game batting statistics">
        <TableHeader>
          <TableRow>
            <TableHead>PLAYER</TableHead>
            <SortableTableHead
              sortKey="PA"
              sortState={sortState}
              onSort={onSort}
            >
              <StatHeading value="PA" />
            </SortableTableHead>
            {SEASON_TABLE_COLUMNS.map((heading) => (
              <SortableTableHead
                key={heading}
                sortKey={heading}
                sortState={sortState}
                onSort={onSort}
              >
                <StatHeading value={heading} />
              </SortableTableHead>
            ))}
            {RATE_TABLE_COLUMNS.map((heading) => (
              <SortableTableHead
                key={heading}
                sortKey={heading}
                sortState={sortState}
                onSort={onSort}
                className={heading === "OPS" ? "bg-warning/10" : undefined}
              >
                <StatHeading value={heading} />
              </SortableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedBattingRows.map(
            ({ name, stats, pa, avg, obp, slg, ops, iso }) => {
              return (
                <TableRow key={name}>
                  <TableCell className="font-bold">{name}</TableCell>
                  <TableCell>{pa}</TableCell>
                  <TableCell>{stats.AB}</TableCell>
                  <TableCell>{stats.H}</TableCell>
                  <TableCell>{stats["1B"]}</TableCell>
                  <TableCell>{stats["2B"]}</TableCell>
                  <TableCell>{stats["3B"]}</TableCell>
                  <TableCell>{stats.HR}</TableCell>
                  <TableCell>{stats.BB}</TableCell>
                  <TableCell>{stats.K}</TableCell>
                  <TableCell>{stats.RBI}</TableCell>
                  <TableCell>{stats.R}</TableCell>
                  <TableCell>{formatRate(avg)}</TableCell>
                  <TableCell>{formatRate(slg)}</TableCell>
                  <TableCell className="bg-warning/10 font-bold text-warning">
                    {formatRate(ops)}
                  </TableCell>
                  <TableCell>{formatRate(obp)}</TableCell>
                  <TableCell>{formatRate(iso)}</TableCell>
                </TableRow>
              );
            },
          )}
        </TableBody>
      </Table>
    </div>
  );
}

const STAT_TITLES: Record<string, string> = {
  PA: "Plate appearances",
  AB: "At bats",
  H: "Hits",
  "1B": "Singles",
  "2B": "Doubles",
  "3B": "Triples",
  HR: "Home runs",
  BB: "Walks",
  K: "Strikeouts",
  RBI: "Runs batted in",
  R: "Runs",
  AVG: "Batting average",
  OBP: "On-base percentage",
  SLG: "Slugging percentage",
  OPS: "On-base plus slugging",
  ISO: "Isolated power",
};

type StatAbbreviation = keyof typeof STAT_TITLES;

function StatHeading({ value }: { value: StatAbbreviation }) {
  return (
    <abbr
      title={STAT_TITLES[value]}
      className="cursor-help decoration-dotted underline-offset-2"
    >
      {value}
    </abbr>
  );
}

function archivedSortValue(
  row: ArchivedBattingRow,
  key: ArchivedSortKey,
): number | undefined {
  switch (key) {
    case "PA":
      return row.pa;
    case "AVG":
      return row.avg;
    case "OBP":
      return row.obp;
    case "SLG":
      return row.slg;
    case "OPS":
      return row.ops;
    case "ISO":
      return row.iso;
    default:
      return row.stats[key as keyof BaseballStats];
  }
}
